import { memo, useCallback, useMemo, useState } from 'react';

import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  type Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ChevronDown, ChevronUp, Circle, type LucideIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ActivityItemFragment } from '@graphql/__generated__/graphql';
import { CollapsibleContent } from '@modules/_shared/components/CollapsibleContent';
import { useActivityContext } from '@modules/_shared/components/feed/ActivityProvider';
import { isProfileInquiryAssignment } from '@modules/_shared/components/feed/utils/activitySource';
import { ActionButton } from '@modules/_shared/components/feed-v2/components/ActionButton';
import { ActivityItemContent } from '@modules/_shared/components/feed-v2/components/ActivitiyItemContent/ActivityItemContent';
import { EmailExtraTimelineItem } from '@modules/_shared/components/feed-v2/components/ActivitiyItemContent/EmailExtraTimelineItem';
import { ActivityItemHeader } from '@modules/_shared/components/feed-v2/components/ActivityItemHeader';
import { ActivityItemLeadSnippet } from '@modules/_shared/components/feed-v2/components/ActivityItemLeadSnippet';
import { ActivityItemLotSnippet } from '@modules/_shared/components/feed-v2/components/ActivityItemLotSnippet';
import { ActivityItemUserSnippet } from '@modules/_shared/components/feed-v2/components/ActivityItemUserSnippet';
import { DateTooltipContent } from '@modules/_shared/components/feed-v2/components/DateTooltipContent';
import { RequalificationChanges } from '@modules/_shared/components/feed-v2/components/RequalificationChanges';
import {
  ACTIVITY_ICONS,
  isOutcomeActivityType,
} from '@modules/_shared/components/feed-v2/constants/activityConfig';
import {
  ghostButtonSx,
  tooltipProps,
} from '@modules/_shared/components/feed-v2/constants/styles';
import { useActivityStatusColor } from '@modules/_shared/components/feed-v2/hooks/useActivityStatusColor';
import type { ActivityGroup } from '@modules/_shared/components/feed-v2/types';
import {
  isAppraisalRequest,
  isProspectionMapAssignment,
  isProspectionMapOutreach,
} from '@modules/_shared/components/feed-v2/utils/activityHelpers';
import { formatFullDate } from '@modules/_shared/components/feed-v2/utils/formatters';
import { parseRequalificationChanges } from '@modules/_shared/components/feed-v2/utils/requalificationParser';
import { FeedDeleteDialog } from '@modules/_shared/components/FeedDeleteDialog';
import { MarkdownRenderer } from '@modules/_shared/components/MardownRenderer';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getInitialsFromDisplayName } from '@modules/_shared/utils/contact';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import { parseChangeExtraData } from '@modules/_shared/utils/lotChangeExtraData';
import { useLocale } from '@shared/hooks/locale';

export const ACTIVITY_ITEM_FRAGMENT = gql(/* GraphQL */ `
  fragment ActivityItem on activities {
    id
    activity_type
    note
    extra_data
    lot_id
    buyer_lead_id
    enquiry_id
    user_id
    enquiry {
      message
    }
    ...ActivityItemHeader
    ...ActivityItemContent
    ...ActivityItemLeadSnippet
    ...ActivityItemLotSnippet
    ...ActivityItemUserSnippet
  }
`);

type Props = {
  group: ActivityGroup;
  isLast: boolean;
  isFirst: boolean;
  isIndirect: boolean;
  isFocused?: boolean;
  onRefetch?: () => void;
  /**
   * When true the item is rendered without the Timeline rail (dot +
   * connectors) and gets outer padding, suited for one-off contexts
   * like the calendar event popup.
   */
  standalone?: boolean;
};

const CollapsibleMarkdownContent = ({ content }: { content: string }) => (
  <Box
    sx={{
      mt: 1,
      p: 1,
      bgcolor: 'grey.100',
      borderRadius: '6px',
      color: 'text.secondary',
      fontSize: '0.75rem',
    }}
  >
    <CollapsibleContent
      nbLinesToShow={1}
      clipType="dots"
      toggleSlotProps={{
        size: 'small',
        disableRipple: true,
        sx: { mt: 0.5, ...ghostButtonSx },
      }}
    >
      <MarkdownRenderer>{content}</MarkdownRenderer>
    </CollapsibleContent>
  </Box>
);

const GroupedActivitiesRow = ({
  activity,
  Icon,
}: {
  activity: ActivityItemFragment;
  Icon: LucideIcon;
}) => {
  const { t, locale } = useLocale();
  const statusColor = useActivityStatusColor({ activity });

  return (
    <Box key={activity.id}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <Icon size={12} color={statusColor} strokeWidth={2.5} />
        <Tooltip
          title={<DateTooltipContent activity={activity} />}
          {...tooltipProps}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              cursor: 'default',
            }}
          >
            {formatFullDate(locale, activity.created_at)}
          </Typography>
        </Tooltip>
      </Stack>
      {/* Show changes for each requalification activity */}
      {activity.activity_type === 'requalification' && (
        <RequalificationChanges
          changes={parseRequalificationChanges(activity.extra_data, t)}
          indented
        />
      )}
    </Box>
  );
};

export const ActivityItem = memo(
  ({
    group,
    isLast,
    isFirst,
    isIndirect,
    isFocused,
    onRefetch,
    standalone = false,
  }: Props) => {
    const [expanded, setExpanded] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const activities = group.activities;
    const activity = activities[0]; // Representative activity
    const { me } = useAppData();
    const profileEnquiryMessage =
      isProfileInquiryAssignment(activity) &&
      activity.lead != null &&
      userCanViewLeadDetails(me, activity.lead)
        ? activity.enquiry?.message?.trim() ?? null
        : null;
    const count = activities.length;
    const { t } = useLocale();

    const [emailExtraAction, setEmailExtraAction] = useState<
      'reply' | 'forward' | null
    >(null);

    const {
      startOutcomeFlow,
      callOutcomeLoading,
      visitOutcomeLoading,
      taskOutcomeLoading,
      activeOutcomeActivityId,
    } = useActivityContext();

    const theme = useTheme();
    const changeVariant = useMemo<
      | 'portal'
      | 'deactivation'
      | 'rejection'
      | 'autoReviewHeld'
      | 'autoReviewApproved'
      | 'fsboBlock'
      | 'fsboUnblock'
      | 'default'
      | null
    >(() => {
      if (activity.activity_type !== 'change' || activity.lot_id == null) {
        return null;
      }
      const extra = parseChangeExtraData(activity.extra_data);
      if (extra == null) {
        return 'default';
      }
      if ('auto_review' in extra) {
        return extra.auto_review.decision === 'approved'
          ? 'autoReviewApproved'
          : 'autoReviewHeld';
      }
      if ('fsbo_block' in extra) {
        return 'fsboBlock';
      }
      if ('fsbo_unblock' in extra) {
        return 'fsboUnblock';
      }
      if ('portal' in extra) {
        return 'portal';
      }
      if ('status' in extra && 'fsbo' in extra) {
        if (extra.status.to === 'paused') {
          return 'deactivation';
        }
        if (extra.status.to === 'rejected') {
          return 'rejection';
        }
      }
      return 'default';
    }, [activity.activity_type, activity.lot_id, activity.extra_data]);

    const Icon =
      ACTIVITY_ICONS[
        isAppraisalRequest(activity)
          ? 'appraisalRequest'
          : isProspectionMapAssignment(activity) ||
            isProspectionMapOutreach(activity)
          ? 'prospectionMapContact'
          : changeVariant === 'portal'
          ? 'changePortal'
          : changeVariant === 'deactivation'
          ? 'changeDeactivation'
          : changeVariant === 'rejection'
          ? 'changeRejection'
          : changeVariant === 'autoReviewHeld'
          ? 'changeAutoReviewHeld'
          : changeVariant === 'autoReviewApproved'
          ? 'changeAutoReviewApproved'
          : changeVariant === 'fsboBlock'
          ? 'changeFsboBlock'
          : changeVariant === 'fsboUnblock'
          ? 'changeFsboUnblock'
          : activity.activity_type
      ] ?? Circle;

    // Check if all activities in the group are done
    const allDone = activities.every(a => a.done);
    const allSuccess = activities.every(a => a.success);

    // Call/Visit/Task outcome handlers
    const handleOutcome = useCallback(
      (type: 'call' | 'visit' | 'task', outcome: 'completed' | 'cancelled') => {
        startOutcomeFlow(type, {
          activityId: activity.id,
          initialOutcome: outcome,
          assignedTo: activity.assigned_to ?? undefined,
          note: activity.note,
        });
      },
      [startOutcomeFlow, activity.id, activity.assigned_to, activity.note],
    );

    // Check if current activity is loading for outcome
    const isOutcomeLoading =
      activeOutcomeActivityId === activity.id &&
      ((activity.activity_type === 'call' && callOutcomeLoading) ||
        (activity.activity_type === 'visit' && visitOutcomeLoading) ||
        (activity.activity_type === 'task' && taskOutcomeLoading));

    // Status-based color: blue (note/email), green (done+success), red (done+failed), orange (pending)
    const statusColor = useActivityStatusColor({
      activity: {
        activity_type: activity.activity_type,
        done: allDone,
        success: allSuccess,
      },
    });

    // FSBO lifecycle events get a fixed tone regardless of the success flag.
    const iconColor =
      changeVariant === 'deactivation' || changeVariant === 'autoReviewHeld'
        ? theme.palette.warning.main
        : changeVariant === 'rejection' || changeVariant === 'fsboBlock'
        ? theme.palette.error.main
        : changeVariant === 'autoReviewApproved' ||
          changeVariant === 'fsboUnblock'
        ? theme.palette.success.main
        : statusColor;

    const negativeActionLabel = useMemo(() => {
      if (activity.activity_type === 'call') {
        return t('Not Reached');
      }
      if (activity.activity_type === 'assignment') {
        return t('Pass');
      }
      return t('Cancel');
    }, [activity.activity_type, t]);

    const positiveActionLabel = useMemo(() => {
      if (activity.activity_type === 'call') {
        return t('Reached');
      }
      if (activity.activity_type === 'assignment') {
        return t('Claim');
      }
      return t('Completed');
    }, [activity.activity_type, t]);

    const outcomeActivityType = isOutcomeActivityType(activity.activity_type)
      ? activity.activity_type
      : null;

    const focusedBorderSx = isFocused
      ? {
          borderColor: 'primary.main',
          boxShadow: (theme: Theme) =>
            `0 0 0 1px ${theme.palette.primary.main}`,
        }
      : undefined;

    return (
      <>
        <TimelineItem sx={{ minHeight: 0, ...(standalone && { p: 1.5 }) }}>
          {!standalone && (
            <TimelineSeparator sx={{ flex: 0 }}>
              {!isFirst && (
                <TimelineConnector
                  sx={{ bgcolor: 'grey.200', height: 14, flexGrow: 0 }}
                />
              )}
              {/* To align the icon with the card title, we need to fill the gap for: paddings (4px + 12px), border (1px) and then we need to align with the 24px height row. Because the icon is 18px height + 6px border, the diff is 24px - 30px = -6px. We divide it by 2 to get the margin top. The top gap should be 4 + 12 + 1 - 3 = 14px */}
              <TimelineDot
                sx={{
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  p: 0,
                  borderTopWidth: 6,
                  borderBottomWidth: 6,
                  my: 0,
                  mt: isFirst ? 1.75 : 0,
                }}
              >
                <Icon size={18} color={iconColor} strokeWidth={2.5} />
              </TimelineDot>
              {!isLast && <TimelineConnector sx={{ bgcolor: 'grey.200' }} />}
            </TimelineSeparator>
          )}

          <TimelineContent
            sx={{
              py: 0.5,
              pl: standalone ? 0 : { xs: 1, sm: 2 },
              pr: 0,
              minHeight: 0,
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                position: 'relative',
                transition: 'all 0.15s ease',
                overflow: 'hidden',
                ...focusedBorderSx,
                '&:hover': focusedBorderSx ?? {
                  borderColor: 'grey.300',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                },
              }}
            >
              <ActivityItemHeader
                activity={activity}
                isIndirect={isIndirect}
                activitiesCount={count}
                done={allDone}
                onDelete={() => setDeleting(true)}
              />

              <ActivityItemContent
                activity={activity}
                isIndirect={isIndirect}
                done={allDone}
                count={count}
                onReplyClicked={() => setEmailExtraAction('reply')}
                onForwardClicked={() => setEmailExtraAction('forward')}
              />

              {/* Note preview */}
              {activity.note && (
                <CollapsibleMarkdownContent content={activity.note} />
              )}
              {profileEnquiryMessage != null &&
                profileEnquiryMessage !== activity.note?.trim() && (
                  <CollapsibleMarkdownContent content={profileEnquiryMessage} />
                )}

              {/* Grouped activities - show more button */}
              {count > 1 && (
                <>
                  <Button
                    size="small"
                    disableRipple
                    onClick={() => setExpanded(!expanded)}
                    endIcon={
                      expanded ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )
                    }
                    sx={{ mt: 1, ...ghostButtonSx }}
                  >
                    {expanded ? t('Hide dates') : t('Show dates')}
                  </Button>

                  <Collapse in={expanded}>
                    <Stack
                      spacing={1}
                      sx={{
                        mt: 1,
                        pl: 1,
                        borderLeft: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {activities.map(act => (
                        <GroupedActivitiesRow
                          key={act.id}
                          activity={act}
                          Icon={Icon}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Action buttons for pending activities (show for primary lead, hide for linked leads) */}
              {!allDone && !isIndirect && outcomeActivityType != null && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <ActionButton
                    variant="negative"
                    disabled={isOutcomeLoading}
                    onClick={() =>
                      handleOutcome(outcomeActivityType, 'cancelled')
                    }
                  >
                    {negativeActionLabel}
                  </ActionButton>
                  <ActionButton
                    variant="positive"
                    disabled={isOutcomeLoading}
                    onClick={() =>
                      handleOutcome(outcomeActivityType, 'completed')
                    }
                  >
                    {positiveActionLabel}
                  </ActionButton>
                </Stack>
              )}

              {/* Snippet display for indirect activities. user_id never
                  co-occurs with lead/lot/buyer/enquiry, so a row with only a
                  user_id is attached to a linked contact (the seller / a
                  property owner) and links back to that contact. */}
              {isIndirect &&
                (activity.lead_id != null ? (
                  <ActivityItemLeadSnippet activity={activity} />
                ) : activity.lot_id != null ||
                  activity.buyer_lead_id != null ||
                  activity.enquiry_id != null ? (
                  <ActivityItemLotSnippet activity={activity} />
                ) : (
                  <ActivityItemUserSnippet activity={activity} />
                ))}
            </Paper>
          </TimelineContent>
        </TimelineItem>

        {activity.activity_type === 'email' && (
          <EmailExtraTimelineItem
            isLast={isLast}
            mode={emailExtraAction}
            activity={activity}
            onClose={() => setEmailExtraAction(null)}
          />
        )}

        <FeedDeleteDialog
          open={deleting}
          onClose={() => setDeleting(false)}
          onDelete={() => {
            setDeleting(false);
            onRefetch?.();
          }}
          title={t('Delete activity')}
          createdAt={activity.created_at}
          activityId={activity.id}
          authorInitials={getInitialsFromDisplayName(
            activity.creator?.full_name,
          )}
        />
      </>
    );
  },
);
